import { FeesApportionParty, PricingTable, PricingTableDocumentTypeFee, PricingTableFormValues } from "@/models";
import { apiRequest, SUPABASE_URL } from "./apiUtils";
import { createApiWrapper } from "./apiWrapper";

export interface PricingTableSearchParams {
  search?: string;
  limit?: number;
  offset?: number;
  management_company_id?: number;
  show_all?: boolean;
  show_none?: boolean;
}

// Create reusable API wrappers with customized list method
const baseTableApi = createApiWrapper<PricingTable>('pricing-tables');
export const pricingTableApi = {
  ...baseTableApi,
  list: async (params: PricingTableSearchParams) => {
    const queryParams = new URLSearchParams();
    
    // Add optional parameters
    if (params.search !== undefined) {
      queryParams.append('search', params.search);
    }
    
    if (params.limit !== undefined) {
      queryParams.append('limit', params.limit.toString());
    }
    
    if (params.offset !== undefined) {
      queryParams.append('offset', params.offset.toString());
    }

    // Add role-based filtering parameters
    if (params.management_company_id !== undefined) {
      queryParams.append('management_company_id', params.management_company_id.toString());
    }

    if (params.show_all !== undefined) {
      queryParams.append('show_all', params.show_all.toString());
    }

    if (params.show_none !== undefined) {
      queryParams.append('show_none', params.show_none.toString());
    }
    
    return apiRequest<PricingTable[]>(`${SUPABASE_URL}/pricing-tables?${queryParams.toString()}`);
  },
    
  get: async (id: number) => 
    apiRequest<PricingTable>(`${SUPABASE_URL}/pricing-tables/${id}`),
    
  create: async (data: PricingTableFormValues & { management_company_id: number }) => 
    apiRequest<PricingTable>(`${SUPABASE_URL}/pricing-tables`, {
      method: 'POST',
      body: data
    }),
    
  update: async (id: number, data: PricingTableFormValues) => 
    apiRequest<PricingTable>(`${SUPABASE_URL}/pricing-tables/${id}`, {
      method: 'PATCH',
      body: data
    }),
    
  delete: async (id: number) => 
    apiRequest<void>(`${SUPABASE_URL}/pricing-tables/${id}`, {
      method: 'DELETE'
    }),
    
  makeDefault: async (id: number) => 
    apiRequest<PricingTable>(`${SUPABASE_URL}/pricing-tables/${id}/make-default`, {
      method: 'POST'
    })
};

export const feesApportionPartyApi = createApiWrapper<FeesApportionParty>('fees-apportion-parties');

// Add custom pricing methods
export const pricingCalculationApi = {
  calculate: async (request: any) => 
    apiRequest<any>(`${SUPABASE_URL}/pricing-calculate`, {
      method: 'POST',
      body: request
    }),
    
  getAssociationPricingTable: async (associationId: number) => 
    apiRequest<PricingTable>(`${SUPABASE_URL}/associations/${associationId}/pricing-table`),
    
  setAssociationPricingTable: async (associationId: number, pricingTableId: number) => 
    apiRequest<void>(`${SUPABASE_URL}/associations/${associationId}/pricing-table`, {
      method: 'PUT',
      body: { pricing_table_id: pricingTableId }
    }),
  
  getPricingTableDocumentTypeFees: async (pricingTableId: number) =>
    apiRequest<PricingTableDocumentTypeFee[]>(`${SUPABASE_URL}/pricing-tables/${pricingTableId}/document-type-fees`)
};
